import { useState, useCallback, useRef, useEffect } from 'react';
import Keycloak from 'keycloak-js';
import { WebSocketMessage } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' 
    ? 'https://localhost:7292'
    : 'https://api.dotnethub.pl'
  );

interface UseWebSocketReturn {
  connected: boolean;
  messages: WebSocketMessage[];
  connect: (keycloak: Keycloak | null) => Promise<void>;
  disconnect: () => void;
}

export const useWebSocket = (): UseWebSocketReturn => {
  const [connected, setConnected] = useState<boolean>(false);
  const [messages, setMessages] = useState<WebSocketMessage[]>([]);
  const wsRef = useRef<WebSocket | null>(null);

  const checkMaintenanceMode = async (): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });

      if (response.status === 503) {
        setMessages(prev => [...prev, { 
          type: 'error', 
          text: '?? System is in maintenance mode. Please try again later.', 
          time: new Date() 
        }]);
        
        // Reload to show maintenance page
        setTimeout(() => window.location.reload(), 2000);
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  };

  const connect = useCallback(async (keycloak: Keycloak | null) => {
    if (!keycloak?.token) {
      console.error('No Keycloak token available for WebSocket connection');
      setMessages(prev => [...prev, { 
        type: 'error', 
        text: 'Authentication required. Please login.', 
        time: new Date() 
      }]);
      return;
    }

    // Check maintenance mode before connecting
    const isMaintenance = await checkMaintenanceMode();
    if (isMaintenance) return;

    try {
      const wsBaseUrl = API_BASE_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      const wsUrl = `${wsBaseUrl}/websockets`;
      const wsUrlWithToken = `${wsUrl}?access_token=${encodeURIComponent(keycloak.token)}`;
      
      console.log('Connecting to WebSocket...');
      
      const ws = new WebSocket(wsUrlWithToken);
      
      ws.onopen = () => {
        console.log('WebSocket connected successfully');
        setConnected(true);
        setMessages(prev => [...prev, { 
          type: 'system', 
          text: `Connected to WebSocket`, 
          time: new Date() 
        }]);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message:', data);
          
          // Ignore ping messages (keep-alive)
          if (data.type === 'ping') {
            console.log('Received keep-alive ping');
            return;
          }
          
          setMessages(prev => [...prev, { 
            type: data.type || 'message', 
            text: JSON.stringify(data, null, 2), 
            time: new Date() 
          }]);
        } catch (error) {
          setMessages(prev => [...prev, { 
            type: 'message', 
            text: event.data, 
            time: new Date() 
          }]);
        }
      };

      ws.onerror = async (error) => {
        console.error('WebSocket error:', error);
        
        // Check if it's maintenance mode
        const isMaintenance = await checkMaintenanceMode();
        if (!isMaintenance) {
          setMessages(prev => [...prev, { 
            type: 'error', 
            text: `WebSocket error. Check console for details.`, 
            time: new Date() 
          }]);
        }
      };

      ws.onclose = async (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        setConnected(false);
        
        // Check if server is in maintenance
        if (event.code === 1006 || event.code === 1001) {
          const isMaintenance = await checkMaintenanceMode();
          if (isMaintenance) return;
        }
        
        let closeMessage = 'Disconnected from WebSocket';
        
        if (event.code === 1000) {
          closeMessage += ' (Normal closure)';
        } else if (event.code === 1006) {
          closeMessage += ' (Abnormal closure - connection lost)';
        } else if (event.code === 1008) {
          closeMessage += ' (Policy violation)';
        } else if (event.code === 401 || event.code === 403) {
          closeMessage += ' (Unauthorized)';
        } else if (event.reason) {
          closeMessage += ` (${event.reason})`;
        } else {
          closeMessage += ` (Code: ${event.code})`;
        }
        
        setMessages(prev => [...prev, { 
          type: 'system', 
          text: closeMessage, 
          time: new Date() 
        }]);
      };

      wsRef.current = ws;

    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      
      // Check maintenance mode on connection failure
      const isMaintenance = await checkMaintenanceMode();
      if (!isMaintenance) {
        setMessages(prev => [...prev, { 
          type: 'error', 
          text: `Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 
          time: new Date() 
        }]);
      }
    }
  }, []);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnecting');
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  return {
    connected,
    messages,
    connect,
    disconnect
  };
};
